/**
 * PIN prompt modal — shown when an API call returns 423 PinRequired.
 *
 * Flow:
 *   1. User clicks a destructive button.
 *   2. API returns 423 → global error handler sets `pendingAction`
 *      (a closure that re-executes the original call).
 *   3. This modal renders, captures the PIN, POSTs verify-pin.
 *   4. On success, it re-runs `pendingAction`.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { Modal } from '../ui/Modal';

export interface PinPromptProps {
  open: boolean;
  onClose: () => void;
  onUnlocked: () => void;
}

export function PinPromptModal({ open, onClose, onUnlocked }: PinPromptProps) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const verifyMut = useMutation({
    mutationFn: async () => {
      await api.post<{ pinUnlockedUntil: string | null }>(
        '/api/auth/verify-pin',
        { pin },
      );
    },
    onSuccess: () => {
      setError(null);
      setPin('');
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      onUnlocked();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError(
            'You have not set a PIN yet. Open Account → change PIN first.',
          );
          return;
        }
        setError(err.message);
        return;
      }
      setError(String(err));
    },
  });

  if (!open) return null;

  return (
    <Modal onClose={onClose} size="sm">
        <Card className="w-full">
          <div>
          <h2 className="mb-1 text-base font-semibold">PIN required</h2>
          <p className="mb-4 text-caption text-ink-muted">
            This action is destructive. Enter your PIN to continue; the
            unlock lasts ~5 minutes.
          </p>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              verifyMut.mutate();
            }}
          >
            <TextInput
              label="PIN"
              type="password"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              name="pin"
            />
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={verifyMut.isPending || pin.length === 0}>
                {verifyMut.isPending ? <Spinner /> : 'Unlock'}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
          </div>
        </Card>
    </Modal>
  );
}
